import { useUserEventsByTypesStream } from '@/api';
import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { ChannelIcon, EngagementIcon } from '@/Components/Dashboard/icons';
import { CHANNEL_APP_TYPES, ENGAGEMENT_CARD_EVENTS, EVENT_PROPERTIES } from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  formatTime,
  getDateLabel,
  getRecentActivityDayWindow,
  HistoryModalTable,
  LoadingCard,
} from '../RecentActivityUtils';

// Native app launches are not shown in the engagement history.
const isNotNativeAppLaunch = (event) => event?.app_type !== CHANNEL_APP_TYPES.NATIVE;

const renderCells = (entry) => {
  const channel = get(entry, EVENT_PROPERTIES.CHANNEL_NAME, '');
  return (
    <div className="row-cell bp-cell">
      <ChannelIcon />
      <span>{isEmpty(channel) ? get(entry, EVENT_PROPERTIES.APP_NAME, '') : channel}</span>
    </div>
  );
};

const ModalContent = ({ useruuid, ...displayProps }) => {
  const trans = useSelector(translator);
  const history = useUserEventsByTypesStream({
    useruuid,
    eventTypes: ENGAGEMENT_CARD_EVENTS,
    filter: isNotNativeAppLaunch,
    ...getRecentActivityDayWindow(),
  });

  return (
    <HistoryModalTable
      {...displayProps}
      {...history}
      headers={[trans('DATE_L'), trans('ACTIVITY')]}
      renderCells={renderCells}
    />
  );
};

ModalContent.propTypes = {
  useruuid: PropTypes.string,
};

export const EngagementCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, data, isLoading, trendData } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  const openHistoryModal = () =>
    openDashboardModal({
      title: (
        <div className="recent-activity-modal-title">
          <EngagementIcon className="recent-activity-modal-title-icon" fill="#005D98" />
          <span>{trans('ENGAGEMENT')}</span>
        </div>
      ),
      content: (
        <ModalContent
          useruuid={useruuid}
          timezone={timezone}
          is24HourFormat={is24HourFormat}
          locale={locale}
        />
      ),
    });

  const handleCardClick = (e) => {
    e.preventDefault();
    openHistoryModal();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openHistoryModal();
    }
  };

  const removeNativeAppLaunch = (obj) => {
    if (obj?.app_launch_event?.app_type === CHANNEL_APP_TYPES.NATIVE) {
      delete obj.app_launch_event;
    }
    return obj;
  };

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};

    const cleaned = removeNativeAppLaunch(data);
    const entries = Object.values(cleaned);

    if (entries.length === 0) return {};

    return entries.reduce((latest, current) =>
      new Date(current.utc_timestamp) > new Date(latest.utc_timestamp) ? current : latest
    );
  }, [data]);

  if (isLoading) return <LoadingCard />;

  const displayText = get(latestEvent, EVENT_PROPERTIES.CHANNEL_NAME, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.UTC_TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const appName = get(latestEvent, EVENT_PROPERTIES.APP_NAME, '');

  const trendPercent = get(
    trendData,
    ['trendResults', 'engagement_bundle', 'engagement_bundle', 'trendPercent'],
    null
  );
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <EngagementIcon className="recent-activity-card-icon" fill="#005D98" />
      <h4 className="recent-activity-card-title">{trans('ENGAGEMENT')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        {timestamp && <p className="recent-activity-time">{`${dateLabel} ${formattedTime}`}</p>}
        {(displayText || appName) && (
          <p className="recent-activity-context">{isEmpty(displayText) ? appName : displayText}</p>
        )}
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('CHANNEL_VIEWS')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

EngagementCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
  trendIsLoading: PropTypes.bool,
};
